import { useState } from 'react';
import { IconButton, Tooltip, Menu, MenuItem, Checkbox, ListItemText, Divider } from '@mui/material';
import PhotographerIcon from './photographerIcon';
import { t } from '../utils/strings';
import { accent } from '../utils/colorDict';

type CreatorOption = { creator: string; creator_heb?: string | null; count: number };

// Surface only creators that represent a whole collection. Smaller per-photographer
// LoC attributions (Larsson, Hole, Visuri, Safieh, "Unknown" — all in the dozens or
// fewer) are noise and shouldn't crowd the menu. A count-based threshold lets new
// collections appear automatically without editing this file each time.
//
// Empirical floor as of this writing:
//   real collections: 550 - 17k items
//   incidental LoC names: 1 - 46 items
// A threshold of 100 cleanly separates the two and leaves comfortable headroom
// for future small collections.
const MIN_CREATOR_COUNT = 100;

/**
 * Compact creator-multi-select dropdown reused by widgets that want their own
 * scoped creator filter (separate from the header's map-wide filter).
 *
 * Renders nothing when there's only one creator in the DB (no useful choice),
 * or when creatorOptions is empty (per-collection sites).
 */
const CreatorFilter = ({
  creatorOptions,
  selected,
  onChange,
  lang,
  iconSize = 22,
  iconColor = 'rgba(0,0,0,0.6)',
}: {
  creatorOptions: CreatorOption[],
  selected: string[],
  onChange: (next: string[]) => void,
  lang: string,
  iconSize?: number,
  iconColor?: string,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isFiltered = Array.isArray(selected) && selected.length > 0;
  // Hide creators below the collection-size floor (individual LoC photographers,
  // "Unknown"). New collections appear automatically once they cross the floor.
  const visibleCreators = Array.isArray(creatorOptions)
    ? creatorOptions.filter((c) => c.count >= MIN_CREATOR_COUNT)
    : [];
  if (visibleCreators.length <= 1) return null;

  return (
    <>
      <Tooltip title={t('creatorFilter', lang)}>
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ p: 0.5, color: isFiltered ? accent : iconColor }}
        >
          <PhotographerIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { maxHeight: 360, minWidth: 220 } }}
      >
        <MenuItem
          onClick={() => onChange([])}
          dir={lang === 'he' ? 'rtl' : 'ltr'}
        >
          <Checkbox
            checked={!isFiltered}
            size="small"
            sx={{ p: 0.5, mr: lang === 'he' ? 0 : 1, ml: lang === 'he' ? 1 : 0 }}
          />
          <ListItemText
            primary={t('allCreators', lang)}
            primaryTypographyProps={{ fontSize: '0.9em', fontWeight: 600 }}
          />
        </MenuItem>
        <Divider />
        {[...visibleCreators]
          .sort((a, b) => {
            const la = (lang === 'he' && a.creator_heb) ? a.creator_heb : a.creator;
            const lb = (lang === 'he' && b.creator_heb) ? b.creator_heb : b.creator;
            return String(la).localeCompare(String(lb), lang === 'he' ? 'he' : 'en', { sensitivity: 'base' });
          })
          .map((c) => {
            const checked = selected.includes(c.creator);
            return (
              <MenuItem
                key={c.creator}
                onClick={() => {
                  const next = checked
                    ? selected.filter((x) => x !== c.creator)
                    : [...selected, c.creator];
                  onChange(next);
                }}
                dir={lang === 'he' ? 'rtl' : 'ltr'}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  sx={{ p: 0.5, mr: lang === 'he' ? 0 : 1, ml: lang === 'he' ? 1 : 0 }}
                />
                <ListItemText
                  primary={(lang === 'he' && c.creator_heb) ? c.creator_heb : c.creator}
                  secondary={`(${c.count})`}
                  primaryTypographyProps={{ fontSize: '0.9em' }}
                  secondaryTypographyProps={{ fontSize: '0.75em', component: 'span', sx: { ml: 0.5 } }}
                  sx={{ '& .MuiListItemText-secondary': { display: 'inline' } }}
                />
              </MenuItem>
            );
          })}
      </Menu>
    </>
  );
};

export default CreatorFilter;
